import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useFocusEffect } from "@react-navigation/native";
import { CameraApiResponse, useCameras } from "@src/features/cameras";
import { getCameraKeyString, isLegacyAppCamera } from "@src/utils/key-parser";
import { useAlarmsByCameras, useUpdateAlarmExceptionsBulk } from "@src/services/api/hooks";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { getTimezoneFromAlarmDetail } from "@src/features/alarms/utils/alarm-utils";
import { buildAlarmSettingsRoutes } from "@src/features/alarms/utils/alarm-settings-routes";
import type { AlarmException } from "@src/services/api/types";
import { pendingExceptionDetailUpdate, type ExceptionParam } from "./exception-shared";

const TRANSITION_MS = 500;
export { TRANSITION_MS };

export function useEditException() {
  const router = useRouter();
  const { tenantId, siteId } = useLocalSearchParams<{ tenantId: string; siteId: string }>();
  const { data: cameras } = useCameras();
  const updateExceptions = useUpdateAlarmExceptionsBulk();
  const routes = useMemo(() => buildAlarmSettingsRoutes(tenantId, siteId), [tenantId, siteId]);

  const {
    exception: exceptionParam,
    includedCameraKeys: includedKeysParam,
    timezone,
    isNew: isNewParam,
  } = useLocalSearchParams<{
    exception: string;
    includedCameraKeys: string;
    timezone: string;
    isNew?: string;
  }>();

  const isNew = isNewParam === "true";

  const originalExceptionData = useMemo<ExceptionParam | null>(() => {
    if (!exceptionParam) return null;
    try {
      return JSON.parse(exceptionParam) as ExceptionParam;
    } catch {
      return null;
    }
  }, [exceptionParam]);

  const [exceptionData, setExceptionData] = useState<ExceptionParam | null>(originalExceptionData);

  useEffect(() => {
    if (originalExceptionData !== null && exceptionData === null) {
      setExceptionData(originalExceptionData);
    }
  }, [originalExceptionData, exceptionData]);

  const initialIncludedKeys = useMemo<Set<string>>(() => {
    if (!includedKeysParam) return new Set();
    try {
      return new Set(JSON.parse(includedKeysParam) as string[]);
    } catch {
      return new Set();
    }
  }, [includedKeysParam]);

  const siteCameras = useMemo(() => {
    if (!cameras || !siteId) return [];
    return cameras.filter((c) => c.site.id === siteId && !isLegacyAppCamera(c));
  }, [cameras, siteId]);

  const allCameraKeys = useMemo(() => siteCameras.map((c) => getCameraKeyString(c)), [siteCameras]);
  const alarmQueries = useAlarmsByCameras(siteId, allCameraKeys);

  const alreadyAssignedKeys = useMemo(() => {
    if (!isNew || !exceptionData) return new Set<string>();
    const assigned = new Set<string>();
    for (let i = 0; i < alarmQueries.length; i++) {
      const config = alarmQueries[i].data;
      if (!config?.settings) continue;
      const cameraKey = allCameraKeys[i];
      const hasMatch = config.settings.exceptions.some(
        (ex) =>
          ex.reason === exceptionData.reason &&
          ex.action === exceptionData.action &&
          JSON.stringify(ex.period) === JSON.stringify(exceptionData.period),
      );
      if (hasMatch) assigned.add(cameraKey);
    }
    return assigned;
  }, [isNew, exceptionData, alarmQueries, allCameraKeys]);

  const [includedKeys, setIncludedKeys] = useState<Set<string>>(initialIncludedKeys);

  const includedKeysSynced = useRef(includedKeysParam !== undefined);
  useEffect(() => {
    if (!includedKeysSynced.current && includedKeysParam !== undefined) {
      includedKeysSynced.current = true;
      setIncludedKeys(initialIncludedKeys);
    }
  }, [includedKeysParam, initialIncludedKeys]);

  const [recentlyRemoved, setRecentlyRemoved] = useState<string[]>([]);
  const [recentlyAdded, setRecentlyAdded] = useState<string[]>([]);

  const cameraByKey = useMemo(() => {
    const map = new Map<string, CameraApiResponse>();
    for (const camera of siteCameras) {
      map.set(getCameraKeyString(camera), camera);
    }
    return map;
  }, [siteCameras]);

  const { included, available } = useMemo(() => {
    const includedSet = new Set<string>();
    const availableSet = new Set<string>();
    for (const camera of siteCameras) {
      const key = getCameraKeyString(camera);
      if (alreadyAssignedKeys.has(key)) continue;
      if (includedKeys.has(key)) includedSet.add(key);
      else availableSet.add(key);
    }

    const incOriginal = siteCameras.filter((c) => {
      const k = getCameraKeyString(c);
      return includedSet.has(k) && !recentlyAdded.includes(k);
    });
    const incAdded = recentlyAdded
      .filter((k) => includedSet.has(k))
      .map((k) => cameraByKey.get(k)!)
      .filter(Boolean);

    const availRemoved = recentlyRemoved
      .filter((k) => availableSet.has(k))
      .map((k) => cameraByKey.get(k)!)
      .filter(Boolean);
    const availOriginal = siteCameras.filter((c) => {
      const k = getCameraKeyString(c);
      return availableSet.has(k) && !recentlyRemoved.includes(k);
    });

    return { included: [...incOriginal, ...incAdded], available: [...availRemoved, ...availOriginal] };
  }, [siteCameras, includedKeys, recentlyAdded, recentlyRemoved, cameraByKey, alreadyAssignedKeys]);

  const tz = timezone ?? "UTC";

  const selectableCameraCount = siteCameras.length - alreadyAssignedKeys.size;
  const cameraLabel =
    includedKeys.size === 0 ? "0 cameras"
    : includedKeys.size === selectableCameraCount ? "All cameras"
    : includedKeys.size === 1 ? "1 camera"
    : `${includedKeys.size} cameras`;

  const isSaving = updateExceptions.isPending;

  const detailsChanged =
    !!exceptionData &&
    !!originalExceptionData &&
    JSON.stringify(exceptionData) !== JSON.stringify(originalExceptionData);

  const addedKeys = useMemo(
    () => new Set([...includedKeys].filter((k) => !initialIncludedKeys.has(k))),
    [includedKeys, initialIncludedKeys],
  );
  const removedKeys = useMemo(
    () => new Set([...initialIncludedKeys].filter((k) => !includedKeys.has(k))),
    [includedKeys, initialIncludedKeys],
  );
  const hasChanges = isNew ? includedKeys.size > 0 : addedKeys.size > 0 || removedKeys.size > 0 || detailsChanged;

  const handleSave = useCallback(() => {
    if (!siteId || !exceptionData) return;
    if (!isNew && !originalExceptionData) return;

    const camerasToUpdate: { cameraKey: string; exceptions: AlarmException[]; timezone: string }[] = [];

    for (let i = 0; i < allCameraKeys.length; i++) {
      const cameraKey = allCameraKeys[i];
      const alarmData = alarmQueries[i]?.data;
      if (!alarmData?.settings) continue;

      const tz = getTimezoneFromAlarmDetail(alarmData) || alarmData.settings.timezone || "UTC";
      const currentExceptions = alarmData.settings.exceptions ?? [];

      if (removedKeys.has(cameraKey)) {
        const originalPeriodKey = JSON.stringify(originalExceptionData?.period);
        const filtered = currentExceptions.filter(
          (ex) =>
            ex.reason !== originalExceptionData?.reason ||
            ex.action !== originalExceptionData?.action ||
            JSON.stringify(ex.period) !== originalPeriodKey,
        );
        camerasToUpdate.push({ cameraKey, exceptions: filtered, timezone: tz });
      } else if (addedKeys.has(cameraKey)) {
        const newException: AlarmException = {
          id: `${exceptionData.reason}-${cameraKey}-${Date.now()}`,
          reason: exceptionData.reason,
          action: exceptionData.action,
          period: exceptionData.period,
          author: null,
        };
        camerasToUpdate.push({ cameraKey, exceptions: [...currentExceptions, newException], timezone: tz });
      } else if (includedKeys.has(cameraKey) && detailsChanged) {
        const originalPeriodKey = JSON.stringify(originalExceptionData!.period);
        const updatedExceptions = currentExceptions.map((ex) => {
          if (
            ex.reason === originalExceptionData!.reason &&
            ex.action === originalExceptionData!.action &&
            JSON.stringify(ex.period) === originalPeriodKey
          ) {
            return { ...ex, reason: exceptionData.reason, action: exceptionData.action, period: exceptionData.period };
          }
          return ex;
        });
        camerasToUpdate.push({ cameraKey, exceptions: updatedExceptions, timezone: tz });
      }
    }

    if (camerasToUpdate.length === 0) return;

    updateExceptions.mutate(
      {
        siteId,
        cameras: camerasToUpdate,
        action: isNew ? "created" : "updated",
        exceptionAction: exceptionData.action,
      },
      {
        onSuccess: () => {
          showSuccessToast({ message: isNew ? "Exception created" : "Exception settings saved" });
          if (isNew) {
            router.dismissTo(routes.manageExceptions as never);
          } else {
            router.back();
          }
        },
        onError: (error) => {
          showErrorToast({ message: "Error", description: "Failed to save exception settings. Please try again." });
          console.error("Failed to save exception settings:", error);
        },
      },
    );
  }, [
    siteId,
    exceptionData,
    originalExceptionData,
    detailsChanged,
    isNew,
    allCameraKeys,
    alarmQueries,
    includedKeys,
    addedKeys,
    removedKeys,
    updateExceptions,
    router,
    routes,
  ]);

  const [transitioning, setTransitioning] = useState<Set<string>>(new Set());

  const toggleCamera = useCallback(
    (cameraKey: string) => {
      if (transitioning.has(cameraKey)) return;
      const isIncluded = includedKeys.has(cameraKey);
      setTransitioning((prev) => new Set(prev).add(cameraKey));
      setTimeout(() => {
        setIncludedKeys((prev) => {
          const next = new Set(prev);
          if (isIncluded) next.delete(cameraKey);
          else next.add(cameraKey);
          return next;
        });
        if (isIncluded) {
          setRecentlyRemoved((prev) => [cameraKey, ...prev.filter((k) => k !== cameraKey)]);
          setRecentlyAdded((prev) => prev.filter((k) => k !== cameraKey));
        } else {
          setRecentlyAdded((prev) => [...prev.filter((k) => k !== cameraKey), cameraKey]);
          setRecentlyRemoved((prev) => prev.filter((k) => k !== cameraKey));
        }
        setTransitioning((prev) => {
          const next = new Set(prev);
          next.delete(cameraKey);
          return next;
        });
      }, TRANSITION_MS);
    },
    [transitioning, includedKeys],
  );

  const removeAll = useCallback(() => {
    const keys = included.map((c) => getCameraKeyString(c));
    setTransitioning(new Set(keys));
    setTimeout(() => {
      setIncludedKeys(new Set());
      setRecentlyRemoved(keys);
      setRecentlyAdded([]);
      setTransitioning(new Set());
    }, TRANSITION_MS);
  }, [included]);

  const includeAll = useCallback(() => {
    const keys = available.map((c) => getCameraKeyString(c));
    setTransitioning(new Set(keys));
    setTimeout(() => {
      const allSelectable = siteCameras.map((c) => getCameraKeyString(c)).filter((k) => !alreadyAssignedKeys.has(k));
      setIncludedKeys(new Set(allSelectable));
      setRecentlyAdded((prev) => [...prev, ...keys]);
      setRecentlyRemoved([]);
      setTransitioning(new Set());
    }, TRANSITION_MS);
  }, [available, siteCameras, alreadyAssignedKeys]);

  useFocusEffect(
    useCallback(() => {
      if (pendingExceptionDetailUpdate.current) {
        setExceptionData(pendingExceptionDetailUpdate.current);
        pendingExceptionDetailUpdate.current = null;
      }
    }, []),
  );

  const navigateToDetails = useCallback(() => {
    if (!exceptionData) return;
    router.push({
      pathname: routes.editExceptionDetails as never,
      params: {
        exception: JSON.stringify(exceptionData),
        includedCameraKeys: includedKeysParam,
        timezone: tz,
      },
    });
  }, [router, exceptionData, includedKeysParam, tz, routes]);

  return {
    isNew,
    exceptionData,
    included,
    available,
    transitioning,
    tz,
    cameraLabel,
    isSaving,
    hasChanges,
    handleSave,
    toggleCamera,
    removeAll,
    includeAll,
    navigateToDetails,
  };
}
